import React, { useState, useRef } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Progress, Badge } from "reactstrap";
import { useTranslation } from "react-i18next";
import { post } from "helpers/api_helper";
import { PATIENT_URL } from "helpers/url_helper";
import { showSuccessAlert, showErroeAlert } from "pages/utils/alertMessages";
import {
  RiFileUploadLine,
  RiFileExcel2Line,
  RiFileTextLine,
  RiDownload2Line,
  RiCheckDoubleLine,
  RiErrorWarningLine,
  RiCloseCircleLine,
  RiUserHeartLine,
  RiCheckLine,
  RiRefreshLine
} from "react-icons/ri";

// Comprehensive Header Mapping dictionary for flexible user spreadsheet column naming
const COLUMN_MAPPINGS = {
  first_name: ["first name", "firstname", "first_name", "fname", "patient name", "name"],
  last_name: ["last name", "lastname", "last_name", "lname", "surname"],
  title: ["title", "salutation", "prefix"],
  mobile: ["mobile", "mobile number", "mobile phone", "phone", "phone number", "contact", "contact number", "cell", "cell phone"],
  email: ["email", "email address", "mail"],
  gender: ["gender", "sex"],
  dob: ["dob", "date of birth", "birth date", "birthdate", "birthday"],
  age: ["age", "years"],
  address: ["address", "street", "residential address", "home address"],
  city: ["city", "town"],
  state: ["state", "province"],
  country: ["country"],
  zip_code: ["zip code", "zip", "pincode", "pin code", "postal code", "post code"],
  blood_group: ["blood group", "blood type", "blood"],
  allergies: ["allergies", "allergy", "medical alert", "alerts"],
  reference_type: ["reference type", "referral type", "source"],
  reference: ["reference", "referred by", "reference name", "referral"],
  case_no: ["case no", "case number", "case_no", "uhid", "mrn", "patient id"]
};

const mapHeaders = (rawHeaders) => {
  const mapping = {};
  rawHeaders.forEach((h, index) => {
    if (!h) return;
    const cleanHeader = String(h).trim().toLowerCase().replace(/[_-]/g, " ");
    for (const [key, aliases] of Object.entries(COLUMN_MAPPINGS)) {
      if (aliases.some(alias => cleanHeader === alias || cleanHeader.includes(alias))) {
        if (!mapping[key]) {
          mapping[key] = index;
        }
      }
    }
  });
  return mapping;
};

// Robust CSV Parsing helper that respects quotes and commas
const parseCSVText = (text) => {
  const lines = text.split(/\r\n|\n|\r/).filter(line => line.trim().length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };

  const parseLine = (line) => {
    const values = [];
    let insideQuotes = false;
    let currentValue = "";

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"' || char === "'") {
        insideQuotes = !insideQuotes;
      } else if (char === "," && !insideQuotes) {
        values.push(currentValue.trim().replace(/^["']|["']$/g, ""));
        currentValue = "";
      } else {
        currentValue += char;
      }
    }
    values.push(currentValue.trim().replace(/^["']|["']$/g, ""));
    return values;
  };

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map(parseLine);
  return { headers, rows };
};

const ImportPatientsModal = ({ isOpen, toggle, onSuccess }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importSummary, setImportSummary] = useState(null);
  const [filterMode, setFilterMode] = useState("all"); // 'all', 'valid', 'invalid'

  const resetModal = () => {
    setFile(null);
    setParsedData([]);
    setIsProcessing(false);
    setIsUploading(false);
    setUploadProgress(0);
    setImportSummary(null);
    setFilterMode("all");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    resetModal();
    toggle();
  };

  // Generate Sample CSV Template
  const handleDownloadTemplate = () => {
    const csvContent = [
      "Title,First Name,Last Name,Mobile Number,Email,Gender,Date of Birth,Age,Address,City,Pin Code,Blood Group,Allergies,Reference",
      "Mr.,Amitabh,Verma,9898011225,amitabh.verma@example.com,Male,1988-05-14,38,B-404 Skyline Residency,Hyderabad,500081,O+,Penicillin,Direct Walk-in",
      "Ms.,Priya,Patel,9898011224,priya.patel@example.com,Female,1992-11-20,33,Flat 12 Greenfield Apts,Hyderabad,500034,B+,None,Google Search",
      "Mr.,Rahul,Sharma,9898011223,rahul.sharma@example.com,Male,1995-03-08,31,Plot 89 Jubilee Hills,Hyderabad,500033,A+,Sulfa Drugs,Dr. Praveen"
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "oralstop_patient_import_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const processRows = (headers, rawRows) => {
    const mapping = mapHeaders(headers);
    const results = [];

    rawRows.forEach((row, idx) => {
      // Skip completely empty lines
      if (row.length === 0 || row.every(val => !val || String(val).trim() === "")) {
        return;
      }

      const getVal = (field) => {
        const colIdx = mapping[field];
        if (colIdx !== undefined && row[colIdx] !== undefined) {
          return String(row[colIdx]).trim();
        }
        return "";
      };

      const firstName = getVal("first_name");
      const lastName = getVal("last_name");
      const rawMobile = getVal("mobile");
      const cleanMobile = rawMobile ? rawMobile.replace(/[^0-9]/g, "").slice(-10) : "";
      const email = getVal("email");
      let gender = getVal("gender");
      if (gender) {
        const gLow = gender.toLowerCase();
        if (gLow.startsWith("f")) gender = "Female";
        else if (gLow.startsWith("m")) gender = "Male";
        else gender = "Other";
      } else {
        gender = "Male";
      }

      const errors = [];
      if (!firstName) {
        errors.push("Missing First Name");
      }
      if (!cleanMobile) {
        errors.push("Missing Mobile Number");
      } else if (cleanMobile.length !== 10) {
        errors.push("Mobile number must be 10 digits");
      }

      let parsedAge = parseInt(getVal("age"), 10);
      let parsedDob = getVal("dob");
      if (parsedDob && !parsedAge) {
        const birthYear = new Date(parsedDob).getFullYear();
        if (!isNaN(birthYear)) {
          parsedAge = Math.max(0, new Date().getFullYear() - birthYear);
        }
      }

      results.push({
        rowNum: idx + 1,
        title: getVal("title") || "Mr.",
        first_name: firstName,
        last_name: lastName,
        mobile: cleanMobile,
        email: email,
        gender: gender,
        dob: parsedDob,
        age: parsedAge || null,
        address: getVal("address"),
        city: getVal("city"),
        zip_code: getVal("zip_code"),
        blood_group: getVal("blood_group"),
        allergies: getVal("allergies"),
        reference: getVal("reference"),
        case_no: getVal("case_no"),
        isValid: errors.length === 0,
        errors: errors
      });
    });

    setParsedData(results);
    setIsProcessing(false);
  };

  const handleFileChange = (e) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);
    setIsProcessing(true);
    setImportSummary(null);

    const fileName = uploadedFile.name.toLowerCase();

    if (fileName.endsWith(".csv")) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const text = evt.target.result;
          const { headers, rows } = parseCSVText(text);
          processRows(headers, rows);
        } catch (err) {
          console.error("CSV parse error:", err);
          showErroeAlert("Failed to parse CSV file. Please check file formatting.");
          setIsProcessing(false);
        }
      };
      reader.readAsText(uploadedFile);
    } else if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
      // Dynamic import for XLSX SheetJS
      import("xlsx").then((XLSX) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
          try {
            const data = new Uint8Array(evt.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            if (jsonData.length > 0) {
              const headers = jsonData[0];
              const rawRows = jsonData.slice(1);
              processRows(headers, rawRows);
            } else {
              setParsedData([]);
              setIsProcessing(false);
            }
          } catch (err) {
            console.error("Excel parse error:", err);
            showErroeAlert("Failed to parse Excel spreadsheet.");
            setIsProcessing(false);
          }
        };
        reader.readAsArrayBuffer(uploadedFile);
      }).catch(err => {
        console.error("SheetJS load error:", err);
        showErroeAlert("Excel parser is loading. Please try uploading CSV or retry in a few seconds.");
        setIsProcessing(false);
      });
    } else {
      showErroeAlert("Unsupported file type. Please upload a .csv, .xlsx, or .xls file.");
      setIsProcessing(false);
    }
  };

  const validRecords = parsedData.filter(d => d.isValid);
  const invalidRecords = parsedData.filter(d => !d.isValid);

  const displayedRecords = parsedData.filter(d => {
    if (filterMode === "valid") return d.isValid;
    if (filterMode === "invalid") return !d.isValid;
    return true;
  });

  const handleExecuteImport = async () => {
    if (validRecords.length === 0) {
      showErroeAlert("No valid patient records found to import.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(30);

    try {
      setUploadProgress(60);
      const res = await post(`${PATIENT_URL}/bulk-import`, {
        patients: validRecords
      });

      setUploadProgress(100);

      if (res?.success) {
        setImportSummary(res.body || res.data || res);
        showSuccessAlert(`Successfully imported ${res?.body?.importedCount || validRecords.length} patient records!`);
        if (typeof onSuccess === "function") {
          onSuccess();
        }
      } else {
        showErroeAlert(res?.message || "Failed to import patients. Please verify the spreadsheet format.");
      }
    } catch (err) {
      console.error("Bulk import failed:", err);
      showErroeAlert(err.message || "Failed to connect to bulk import service.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl" centered backdrop="static" className="patient-import-modal">
      <ModalHeader toggle={handleClose} className="border-bottom pb-3 bg-light">
        <div className="d-flex align-items-center gap-2">
          <div className="avatar-xs rounded-circle bg-success text-white d-flex align-items-center justify-content-center">
            <RiFileUploadLine className="font-size-18" />
          </div>
          <div>
            <h5 className="modal-title font-weight-bold mb-0 text-slate-800">
              {t("Bulk Import Patients via CSV / Excel")}
            </h5>
            <p className="text-muted font-size-12 mb-0">
              {t("Rapidly import patient contact registries, demographics, and case details from spreadsheets")}
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="p-4">
        {/* Step 1: Template Download Banner */}
        <div className="p-3 mb-4 rounded-3 border bg-soft-primary d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="avatar-sm rounded-circle bg-white text-primary d-flex align-items-center justify-content-center shadow-sm font-size-20">
              <RiFileExcel2Line />
            </div>
            <div>
              <h6 className="font-weight-bold text-primary mb-1">
                {t("Download Standard Spreadsheet Template")}
              </h6>
              <p className="text-muted font-size-12 mb-0">
                {t("Use our pre-configured CSV/Excel format with mandatory fields (First Name, 10-digit Mobile Number) to avoid formatting errors.")}
              </p>
            </div>
          </div>
          <Button
            color="primary"
            className="d-inline-flex align-items-center gap-1 font-weight-semibold shadow-sm"
            onClick={handleDownloadTemplate}
          >
            <RiDownload2Line /> {t("Download Sample Template (.CSV)")}
          </Button>
        </div>

        {/* Step 2: Drag and Drop Upload Zone */}
        {!importSummary && (
          <div
            className={`import-dropzone p-4 text-center rounded-3 border-2 border-dashed ${file ? "border-success bg-soft-success" : "border-slate bg-light"}`}
            style={{ cursor: "pointer", transition: "all 0.2s ease" }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".csv, .xlsx, .xls"
              onChange={handleFileChange}
            />
            <div className="my-2">
              <RiFileUploadLine className={`font-size-42 ${file ? "text-success" : "text-muted"}`} />
              <h6 className="mt-2 font-weight-bold text-dark">
                {file ? file.name : t("Click or drag CSV / Excel file here to upload")}
              </h6>
              <p className="text-muted font-size-12 mb-0">
                {file ? `${(file.size / 1024).toFixed(1)} KB • Click to change file` : t("Supported formats: .CSV, .XLSX, .XLS (Up to 1,000 patients per batch)")}
              </p>
            </div>
          </div>
        )}

        {isProcessing && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-2 text-muted font-size-13">{t("Parsing spreadsheet rows and validating data...")}</p>
          </div>
        )}

        {/* Step 3: Interactive Validation Preview */}
        {!isProcessing && parsedData.length > 0 && !importSummary && (
          <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
              <div className="d-flex align-items-center gap-2">
                <span className="font-weight-bold text-slate-800 font-size-14">
                  {t("Data Validation Summary")}:
                </span>
                <button
                  type="button"
                  className={`btn btn-sm ${filterMode === "all" ? "btn-dark" : "btn-light"} rounded-pill font-size-12`}
                  onClick={() => setFilterMode("all")}
                >
                  {t("All Rows")} ({parsedData.length})
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filterMode === "valid" ? "btn-success" : "btn-soft-success text-success"} rounded-pill font-size-12`}
                  onClick={() => setFilterMode("valid")}
                >
                  <RiCheckLine /> {t("Ready to Import")} ({validRecords.length})
                </button>
                {invalidRecords.length > 0 && (
                  <button
                    type="button"
                    className={`btn btn-sm ${filterMode === "invalid" ? "btn-danger" : "btn-soft-danger text-danger"} rounded-pill font-size-12`}
                    onClick={() => setFilterMode("invalid")}
                  >
                    <RiErrorWarningLine /> {t("Validation Errors")} ({invalidRecords.length})
                  </button>
                )}
              </div>

              <div className="text-muted font-size-12">
                {t("Showing")} {displayedRecords.length} {t("of")} {parsedData.length} {t("records")}
              </div>
            </div>

            {/* Preview Table */}
            <div className="table-responsive border rounded-3" style={{ maxHeight: "320px", overflowY: "auto" }}>
              <table className="table table-hover table-nowrap align-middle mb-0 font-size-13">
                <thead className="table-light sticky-top bg-light">
                  <tr>
                    <th style={{ width: "40px" }} className="text-center">#</th>
                    <th>{t("Status")}</th>
                    <th>{t("Patient Name")}</th>
                    <th>{t("Mobile Phone")}</th>
                    <th>{t("Gender / Age")}</th>
                    <th>{t("City / Address")}</th>
                    <th>{t("Reference")}</th>
                    <th>{t("Issue / Notes")}</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedRecords.map((r, i) => (
                    <tr key={i} className={r.isValid ? "" : "table-warning bg-soft-warning"}>
                      <td className="text-center text-muted font-weight-semibold">{r.rowNum}</td>
                      <td>
                        {r.isValid ? (
                          <Badge color="success" className="px-2 py-1 font-size-11">
                            <RiCheckLine /> Ready
                          </Badge>
                        ) : (
                          <Badge color="danger" className="px-2 py-1 font-size-11">
                            <RiCloseCircleLine /> Invalid
                          </Badge>
                        )}
                      </td>
                      <td className="font-weight-semibold text-slate-900">
                        {r.title} {r.first_name} {r.last_name}
                      </td>
                      <td>
                        {r.mobile ? (
                          <span className="font-weight-medium text-slate-800">{r.mobile}</span>
                        ) : (
                          <span className="text-danger fst-italic">Missing Mobile</span>
                        )}
                      </td>
                      <td>
                        <span className="badge bg-light text-dark me-1">{r.gender || "Male"}</span>
                        {r.age ? <span className="text-muted">{r.age} yrs</span> : null}
                      </td>
                      <td className="text-muted font-size-12">
                        {r.city ? `${r.city} • ` : ""}{r.address || "—"}
                      </td>
                      <td className="text-muted font-size-12">{r.reference || "Direct Walk-in"}</td>
                      <td>
                        {r.isValid ? (
                          <span className="text-success font-size-11">Passed validation checks</span>
                        ) : (
                          <span className="text-danger font-weight-semibold font-size-11">
                            {r.errors.join(", ")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Step 4: Import Summary Post-Upload */}
        {importSummary && (
          <div className="p-4 rounded-3 border bg-soft-success text-center">
            <div className="avatar-md rounded-circle bg-success text-white d-flex align-items-center justify-content-center mx-auto mb-3 shadow">
              <RiCheckDoubleLine className="font-size-28" />
            </div>
            <h4 className="font-weight-bold text-success mb-1">
              {t("Import Completed Successfully!")}
            </h4>
            <p className="text-muted font-size-13 mb-3">
              {t("Batch execution report and created patient records summary:")}
            </p>

            <Row className="g-3 my-2 justify-content-center">
              <Col md={3}>
                <div className="p-3 bg-white rounded-3 border shadow-sm">
                  <div className="text-muted font-size-11 font-weight-bold text-uppercase">{t("Total Rows")}</div>
                  <div className="font-size-22 font-weight-bold text-dark">{importSummary.total || 0}</div>
                </div>
              </Col>
              <Col md={3}>
                <div className="p-3 bg-white rounded-3 border shadow-sm">
                  <div className="text-success font-size-11 font-weight-bold text-uppercase">{t("Imported")}</div>
                  <div className="font-size-22 font-weight-bold text-success">{importSummary.importedCount || 0}</div>
                </div>
              </Col>
              <Col md={3}>
                <div className="p-3 bg-white rounded-3 border shadow-sm">
                  <div className="text-warning font-size-11 font-weight-bold text-uppercase">{t("Skipped / Duplicates")}</div>
                  <div className="font-size-22 font-weight-bold text-warning">{importSummary.skippedCount || 0}</div>
                </div>
              </Col>
            </Row>

            {importSummary.duplicates?.length > 0 && (
              <div className="alert alert-warning text-start font-size-12 mt-3 mb-0">
                <strong>{t("Duplicate Notice:")}</strong> {importSummary.duplicates.length} {t("patient records were skipped because their mobile numbers were already registered in this clinic.")}
              </div>
            )}
          </div>
        )}

        {isUploading && (
          <div className="my-3">
            <div className="d-flex justify-content-between font-size-12 font-weight-bold mb-1 text-primary">
              <span>{t("Importing records into database...")}</span>
              <span>{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} color="success" animated striped style={{ height: "8px" }} />
          </div>
        )}
      </ModalBody>

      <ModalFooter className="border-top bg-light">
        {importSummary ? (
          <Button color="success" className="px-4 font-weight-bold" onClick={handleClose}>
            {t("Close & Refresh Patient Registry")}
          </Button>
        ) : (
          <div className="d-flex align-items-center justify-content-between w-100">
            <Button color="secondary" outline onClick={handleClose} disabled={isUploading}>
              {t("Cancel")}
            </Button>
            <Button
              color="success"
              className="d-inline-flex align-items-center gap-1 px-4 font-weight-bold shadow-sm"
              disabled={validRecords.length === 0 || isUploading || isProcessing}
              onClick={handleExecuteImport}
            >
              {isUploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" />
                  {t("Importing...")}
                </>
              ) : (
                <>
                  <RiFileUploadLine className="font-size-16" />
                  {t("Confirm & Import")} ({validRecords.length} {t("Patients")})
                </>
              )}
            </Button>
          </div>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ImportPatientsModal;
